import { useCallback } from 'react';

import isBitcoinAddress, { AddressType, getAddressInfo } from 'bitcoin-address-validation';
import { BtcAddressType, getBtcAddressType } from 'market-maker.cm/cweb_dist/offchain/shared';
import validator from 'multicoin-address-validator';
import { TronWeb } from 'tronweb';
import { isAddress as isEvmAddress, type IsAddressOptions } from 'viem';

import { Currency } from '@/constants';
import { useValidateCoinwebAddress } from '@/networks/coinweb';
import {
  isBtcCurrency,
  isErc20Currency,
  isEvmCurrency,
  isTronCurrency,
  isTrc20Currency,
  isLtcCurrency,
  isDogeCurrency,
  isCwebCurrency,
} from '@/utils';

export const supportedBtcAddressTypes = [
  AddressType.p2pkh,
  AddressType.p2sh,
  AddressType.p2wpkh,
  AddressType.p2wsh,
  AddressType.p2tr,
];
export const supportedLtcAddressTypes = [
  BtcAddressType.p2pkh,
  BtcAddressType.p2sh,
  BtcAddressType.p2wpkh,
  BtcAddressType.p2wsh,
  BtcAddressType.p2tr,
];
export const supportedDogeAddressTypes = [BtcAddressType.p2pkh, BtcAddressType.p2sh];

const useValidateAddress = () => {
  const isCoinwebAddress = useValidateCoinwebAddress();

  const validateAddress = useCallback((address: string, token: Currency, options?: IsAddressOptions) => {
    if (isBtcCurrency(token)) {
      if (isBitcoinAddress(address)) {
        const btcInfo = getAddressInfo(address);

        return supportedBtcAddressTypes.includes(btcInfo.type);
      }

      return false;
    }

    if (isLtcCurrency(token)) {
      if (validator.validate(address, 'ltc')) {
        const addressType = getBtcAddressType(address, 'ltc');

        return supportedLtcAddressTypes.includes(addressType);
      }

      return false;
    }

    if (isDogeCurrency(token)) {
      if (validator.validate(address, 'doge')) {
        const addressType = getBtcAddressType(address, 'dog');

        return supportedDogeAddressTypes.includes(addressType);
      }

      return false;
    }

    if (isEvmCurrency(token) || isErc20Currency(token)) {
      return isEvmAddress(address, options as IsAddressOptions);
    }

    if (isTronCurrency(token) || isTrc20Currency(token)) {
      try {
        return TronWeb.isAddress(address);
      } catch {
        return false;
      }
    }

    if (isCwebCurrency(token)) {
      return isCoinwebAddress(address);
    }

    return false;
  }, []);

  return validateAddress;
};

export default useValidateAddress;
